import { useEffect, useMemo, useRef, useState } from 'react';

import { arrayMove } from '@dnd-kit/sortable';
import useWebSocket from 'lanetalk-websocket';
import { generatePath, useNavigate } from 'react-router-dom';

import routesPath from '@/shared/constants/routesPath';
import useBowlingCentersStore from '@/shared/store/bowlingCenters';
import useSettingsStore from '@/shared/store/settings';
// import useUserStore from '@/shared/store/user';
import { getPlayerId } from '@/shared/utils';

const BREAKPOINTS = [
  { width: 640, base: 1, maxColumns: 1 },
  { width: 1024, base: 2, maxColumns: 2 },
  { width: 1200, base: 3, maxColumns: 5 },
  { width: 1440, base: 3, maxColumns: 6 },
  { width: 1920, base: 3, maxColumns: 8 },
];

const LANE_HEIGHT = 150;
const PADDING_LEFT_RIGHT = 32; // Total padding left and right
const GAP = 16; // Gap between columns
const FINISHED_GAMES_TIMEOUT = import.meta.env.DEV ? 1 : 2; // In minutes
const RECONNECT_THRESHOLD = import.meta.env.DEV ? 1 : 10; // In minutes

const getBreakpointSettings = () => {
  const viewportWidth = window.innerWidth;

  for (const breakpoint of BREAKPOINTS) {
    const { width, base, maxColumns } = breakpoint;
    if (viewportWidth < width) {
      return {
        base,
        maxColumns,
      };
    }
  }
  // default settings for ultra-wide screens, starting from 1920
  return {
    base: 3,
    maxColumns: 10,
  };
};

const isGameFinished = (throws) => {
  if (throws.length === 21) {
    return true;
  }

  if (throws.length === 20) {
    return !(throws[18] === 'X' || throws[19] === 'X' || throws[19] === '/');
  }

  return false;
};

export default function useLive() {
  const navigate = useNavigate();
  const { current: bc } = useBowlingCentersStore();
  const settings = useSettingsStore();
  // const { user } = useUserStore();
  const wsRef = useRef();
  const { isConnected, lastMessage } = useWebSocket({
    url: import.meta.env.VITE_WEBSOCKET_URL,
    channel: `LiveScores:${bc?.uuid}`,
    apiKey: import.meta.env.VITE_WEBSOCKET_TOKEN,
    debug: !!import.meta.env.DEV,
    ref: wsRef,
  });
  const [followPlayers, setFollowPlayers] = useState([]);
  const [lanes, setLanes] = useState(new Map());
  const [baseColumns, setBaseColumns] = useState(3);

  const [gridRowsHeight, setGridRowsHeight] = useState(null);
  const listRef = useRef(null);
  const timeoutIdsRef = useRef(new Map());
  const lastMessageTimeRef = useRef();

  /** EFFECTS */
  useEffect(() => {
    if (lastMessage?.type === 2) {
      const processedLanes = new Map();
      // Initialize all lanes with an empty array to accumulate players
      for (let i = 1; i <= bc.lanes; i++) {
        processedLanes.set(i, []);
      }

      lastMessage.data.lanes.forEach((playerInfo) => {
        if (processedLanes.has(playerInfo.lane)) {
          processedLanes
            .get(playerInfo.lane)
            .push({ ...playerInfo, id: getPlayerId(playerInfo) });
        }
      });
      setLanes(processedLanes);

      // Check if hideInactiveLanes is true and update active lanes
      if (settings.hideInactiveLanes) {
        const activeLanes = Array.from(processedLanes)
          .filter(([, players]) => players.length > 0)
          .map(([laneNumber]) => laneNumber);
        updateSettings({
          lanes: activeLanes,
        });
      } else {
        // if settings.lanes is not empty, and if some of the lanes are not in the processedLanes, then update the lanes
        if (
          settings.lanes.length > 0 &&
          !settings.lanes.every((lane) => processedLanes.has(lane))
        ) {
          updateSettings({
            lanes: [],
          });
        }
      }
    }

    if (lastMessage?.type === 5) {
      const updatedLanes = new Map(lanes);
      const laneToUpdate = lastMessage.data.lane;
      const playerToUpdateId = getPlayerId(lastMessage.data);

      const updatedPlayers =
        updatedLanes.get(laneToUpdate)?.map((player) => {
          if (player.id === playerToUpdateId) {
            return { ...player, ...lastMessage.data };
          }
          return player;
        }) || [];

      // If the player is not found, add the new player
      if (!updatedPlayers.some((player) => player.id === playerToUpdateId)) {
        updatedPlayers.push({ ...lastMessage.data, id: playerToUpdateId });
      }
      updatedLanes.set(laneToUpdate, updatedPlayers);
      // Finally update the lanes with the new data
      setLanes(updatedLanes);

      // Handle finished games.
      // Ideally the first and last players should have finished their games
      const lastPlayer = updatedPlayers[updatedPlayers.length - 1];
      const firstPlayer = updatedPlayers[0];
      const shouldClearGames =
        lastPlayer &&
        isGameFinished(lastPlayer.throws) &&
        firstPlayer &&
        isGameFinished(firstPlayer.throws);

      if (shouldClearGames) {
        console.debug('Finished games in lane', laneToUpdate);

        // Clear any existing timeout for this lane
        if (timeoutIdsRef.current.has(laneToUpdate)) {
          clearTimeout(timeoutIdsRef.current.get(laneToUpdate));
        }

        const timeoutId = setTimeout(
          () => {
            filterFinishedGames(laneToUpdate);
            timeoutIdsRef.current.delete(laneToUpdate);
          },
          FINISHED_GAMES_TIMEOUT * 60 * 1000
        );
        timeoutIdsRef.current.set(laneToUpdate, timeoutId);
      }

      // update the last message time
      lastMessageTimeRef.current = Date.now();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastMessage]);

  // Side effect to update the list of followed players
  useEffect(() => {
    if (settings.followPlayers.length > 0 && lanes.size > 0) {
      const currentPlayers = Array.from(lanes.values()).flatMap(
        (player) => player
      );
      const playersToFollow = currentPlayers.filter((player) =>
        settings.followPlayers.includes(getPlayerId(player))
      );

      setFollowPlayers(playersToFollow);

      // If no players match, update the settings.followPlayers to an empty array
      if (playersToFollow.length === 0) {
        updateSettings({ followPlayers: [] });
      }
    } else if (
      settings.followPlayers.length === 0 &&
      followPlayers.length !== 0
    ) {
      setFollowPlayers([]); // Reset followPlayers only if it's not already empty
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lanes, settings.followPlayers]);

  // Side effect to update the grid template rows
  useEffect(() => {
    if (listRef.current && lanes.size > 0) {
      const rows = Array.from(listRef.current.children);
      const heights = [];
      const columns = settings.columns || baseColumns;
      let currentRow = [];
      let maxHeight = 0;

      rows.forEach((row, index) => {
        const rowHeight = row.firstElementChild.getBoundingClientRect().height;
        maxHeight = Math.max(maxHeight, rowHeight);
        currentRow.push(row);

        if ((index + 1) % columns === 0 || index === rows.length - 1) {
          heights.push(maxHeight);
          currentRow = [];
          maxHeight = 0;
        }
      });

      const filteredZeroHeights = heights
        .map(
          (height) =>
            height < LANE_HEIGHT ? `${LANE_HEIGHT}px` : `${height}px` // 84 = min heigth for a single player
        )
        .join(' ');
      setGridRowsHeight(filteredZeroHeights);
    }
  }, [
    settings.columns,
    settings.lanes.length,
    settings.showPinLeaves,
    baseColumns,
    lanes,
  ]);

  // Side effect to update the base columns
  // and reconnect on document focus
  useEffect(() => {
    const handleResize = () => {
      const { base, maxColumns } = getBreakpointSettings();
      setBaseColumns(base);
      updateSettings({
        columns: settings.columns > maxColumns ? base : settings.columns,
        maxColumns,
      });
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const now = Date.now();
        const timeSinceLastMessage = now - lastMessageTimeRef.current;
        const threshold = Date.now() + RECONNECT_THRESHOLD * 60 * 1000;

        if (timeSinceLastMessage > threshold) {
          // Reconnect WebSocket or refresh data
          console.debug('Tab regained focus, reconnecting WebSocket...');
          wsRef.current.connection.disconnect();
          wsRef.current.connection.connect();
        }
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clean the timeouts when the component unmounts
  useEffect(() => {
    const timeoutIds = timeoutIdsRef.current;
    return () => {
      timeoutIds.forEach((timeoutId) => clearTimeout(timeoutId));
      timeoutIds.clear();
      console.debug('Cleared all timeouts');
    };
  }, []);
  /** END EFFECTS */

  /** METHODS */
  const updateSettings = (newValues) => {
    Object.entries(newValues).forEach(([key, value]) => {
      settings.onUpdateSettings(key, value);
    });
  };
  const onHideInactiveLanes = () => {
    let activeLanes = [];

    if (!settings.hideInactiveLanes) {
      activeLanes = [...lanes]
        .filter(([, players]) => players.length > 0)
        .map(([laneNumber]) => laneNumber);
    }

    updateSettings({
      hideInactiveLanes: !settings.hideInactiveLanes,
      lanes: activeLanes,
    });
  };
  const onFilterLanes = (lane) => {
    if (settings.lanes.includes(lane)) {
      updateSettings({
        lanes: settings.lanes.filter((l) => l !== lane),
      });
    } else {
      updateSettings({
        lanes: [...settings.lanes, lane],
        hideInactiveLanes: false,
      });
    }
  };
  const onDragEnd = (event) => {
    const { active, over } = event;
    if (!over) return;
    if (active?.id !== over?.id) {
      const oldIndex = followPlayers.findIndex(
        (player) => player.id === active.id
      );
      const newIndex = followPlayers.findIndex(
        (player) => player.id === over.id
      );
      const newFollowPlayers = arrayMove(followPlayers, oldIndex, newIndex);
      setFollowPlayers(newFollowPlayers);
    }
  };
  const onSeeFinishedGames = () => {
    const currentPath = generatePath(routesPath.LIVESCORING, {
      uuid: bc?.uuid || '',
    });
    navigate(`${currentPath}?tab=finished`);
  };
  const filterFinishedGames = (laneNumber) => {
    setLanes((prevLanes) => {
      const updatedLanes = new Map(prevLanes);
      // first get the lane
      const lane = updatedLanes.get(laneNumber);
      if (!lane) return;

      // then filter the players
      const activePlayers = lane.filter(
        (player) => !isGameFinished(player.throws)
      );
      updatedLanes.set(laneNumber, activePlayers);

      if (activePlayers.length === 0) {
        console.debug('Cleared finished games in lane', laneNumber);
        if (settings.hideInactiveLanes) {
          onFilterLanes(laneNumber);
        }
      }

      return updatedLanes;
    });
  };
  /** END METHODS */

  // Calculate the scale factor based on the number of columns
  const scaleFactor = useMemo(() => {
    if (settings.columns === 1 && window.innerWidth < 640) {
      const smallScreenScaleFactor = window.innerWidth / 640;
      return smallScreenScaleFactor < 1 ? smallScreenScaleFactor : 1;
    }

    return settings.columns
      ? (baseColumns / settings.columns) * (window.innerWidth / 1920)
      : 1;
  }, [settings.columns, baseColumns]);

  const laneWidth =
    (window.innerWidth - PADDING_LEFT_RIGHT - GAP * (settings.columns - 1)) /
    settings.columns /
    scaleFactor;

  return {
    isLoading: !isConnected,
    lanes: Array.from(lanes.entries()),
    settings: {
      columns: settings.columns,
      lanes: settings.lanes,
      showPinLeaves: settings.showPinLeaves,
      // showPaywall: user?.subscriptions.length === 0 || true,
      listRef,
      gridTemplateRows: gridRowsHeight,
      scaleFactor,
      laneWidth,
      baseColumns,
      gap: GAP,
    },
    hasNoGames: Array.from(lanes.values()).every(
      (players) => players.length === 0
    ),
    hasError: lastMessage?.error,
    followPlayers,
    onHideInactiveLanes,
    onFilterLanes,
    onDragEnd,
    onSeeFinishedGames,
  };
}
